import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${(process.env.REACT_APP_BACKEND_URL || 'https://bobine.live')}/api`;
const serif = "'Georgia', 'Times New Roman', serif";
const montserrat = "'Montserrat', 'Arial', sans-serif";

/* ── Icone Tamis SVG vectorielle (toujours nette, fond transparent) ── */
const TamisIcon = ({ size = 100 }) => (
  <svg width={size} height={size * 1.15} viewBox="0 0 100 115" fill="none" style={{ display: 'block', margin: '0 auto' }}>
    <defs>
      <linearGradient id="tamisGold" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#E8C368"/>
        <stop offset="50%" stopColor="#D4AF37"/>
        <stop offset="100%" stopColor="#B8962E"/>
      </linearGradient>
      <clipPath id="tamisMesh">
        <ellipse cx="50" cy="42" rx="31" ry="26"/>
      </clipPath>
      <filter id="tamisGlow">
        <feGaussianBlur stdDeviation="4" result="blur"/>
        <feMerge>
          <feMergeNode in="blur"/>
          <feMergeNode in="SourceGraphic"/>
        </feMerge>
      </filter>
    </defs>
    {/* Glow halo */}
    <ellipse cx="50" cy="42" rx="38" ry="34" fill="rgba(212,175,55,0.06)"/>
    {/* Outer rim */}
    <ellipse cx="50" cy="42" rx="35" ry="30" stroke="url(#tamisGold)" strokeWidth="2.5" fill="none" filter="url(#tamisGlow)"/>
    {/* Inner rim */}
    <ellipse cx="50" cy="42" rx="31" ry="26" stroke="rgba(212,175,55,0.25)" strokeWidth="0.8" fill="none"/>
    {/* Mesh grid (clipped) */}
    <g clipPath="url(#tamisMesh)" opacity="0.3">
      <line x1="18" y1="24" x2="82" y2="24" stroke="#D4AF37" strokeWidth="0.5"/>
      <line x1="18" y1="32" x2="82" y2="32" stroke="#D4AF37" strokeWidth="0.5"/>
      <line x1="18" y1="40" x2="82" y2="40" stroke="#D4AF37" strokeWidth="0.5"/>
      <line x1="18" y1="48" x2="82" y2="48" stroke="#D4AF37" strokeWidth="0.5"/>
      <line x1="18" y1="56" x2="82" y2="56" stroke="#D4AF37" strokeWidth="0.5"/>
      <line x1="28" y1="10" x2="28" y2="74" stroke="#D4AF37" strokeWidth="0.5"/>
      <line x1="38" y1="10" x2="38" y2="74" stroke="#D4AF37" strokeWidth="0.5"/>
      <line x1="50" y1="10" x2="50" y2="74" stroke="#D4AF37" strokeWidth="0.5"/>
      <line x1="62" y1="10" x2="62" y2="74" stroke="#D4AF37" strokeWidth="0.5"/>
      <line x1="72" y1="10" x2="72" y2="74" stroke="#D4AF37" strokeWidth="0.5"/>
    </g>
    {/* Decorative dots at intersections */}
    <g clipPath="url(#tamisMesh)" opacity="0.15">
      {[24,32,40,48,56].map(y =>
        [28,38,50,62,72].map(x =>
          <circle key={`${x}-${y}`} cx={x} cy={y} r="1" fill="#D4AF37"/>
        )
      )}
    </g>
    {/* Handle */}
    <line x1="50" y1="72" x2="50" y2="98" stroke="url(#tamisGold)" strokeWidth="2.5" strokeLinecap="round"/>
    {/* Handle knob */}
    <circle cx="50" cy="103" r="5" stroke="url(#tamisGold)" strokeWidth="1.5" fill="none"/>
    <circle cx="50" cy="103" r="1.5" fill="#D4AF37"/>
  </svg>
);

const PassageAuTamis = ({ token, onComplete, sounds }) => {
  const [photos, setPhotos] = useState([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ kept: 0, deleted: 0 });
  const [imageLoaded, setImageLoaded] = useState(false);

  // Drag state
  const [dragX, setDragX] = useState(0);
  // Exit animation direction
  const [exitDir, setExitDir] = useState(null); // 'left' | 'right'

  const startXRef = useRef(null);
  const processingRef = useRef(false);
  const headersRef = useRef({ Authorization: `Bearer ${token}` });

  useEffect(() => { headersRef.current = { Authorization: `Bearer ${token}` }; }, [token]);

  useEffect(() => {
    axios.get(`${API}/sas/photos`, { headers: headersRef.current }).then(res => {
      setPhotos(res.data.photos || []);
      setLoading(false);
    }).catch(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset imageLoaded when currentIdx changes
  useEffect(() => { setImageLoaded(false); }, [currentIdx]);

  // Pre-load next 2 images when currentIdx changes
  useEffect(() => {
    if (!photos.length) return;
    for (let i = 1; i <= 2; i++) {
      const p = photos[currentIdx + i];
      if (p) {
        const img = new Image();
        img.src = `${(process.env.REACT_APP_BACKEND_URL || 'https://bobine.live')}${p.image_url}?token=${token}`;
      }
    }
  }, [currentIdx, photos, token]);

  const currentPhoto = photos[currentIdx];
  const nextPhoto = photos[currentIdx + 1];
  const remaining = photos.length - currentIdx;
  const isExiting = !!exitDir;

  /* ── Swipe action ── */
  const handleAction = useCallback((action) => {
    if (processingRef.current || !photos[currentIdx]) return;
    processingRef.current = true;

    // Update stats immediately (optimistic)
    setStats(prev => ({
      kept: prev.kept + (action === 'coffre' ? 1 : 0),
      deleted: prev.deleted + (action === 'supprimer' ? 1 : 0),
    }));

    // Fire API non-blocking — mais tracker les erreurs
    axios.post(`${API}/sas/trier`, {
      photo_id: photos[currentIdx].id, action
    }, { headers: headersRef.current }).then(res => {
      if (res.data?.status === 'quota_reached') {
        setStats(prev => ({ kept: prev.kept - 1, deleted: prev.deleted }));
      }
    }).catch(() => {
      setStats(prev => ({
        kept: prev.kept - (action === 'coffre' ? 1 : 0),
        deleted: prev.deleted - (action === 'supprimer' ? 1 : 0),
      }));
    });

    // Clear drag and trigger exit animation
    setDragX(0);
    setExitDir(action === 'coffre' ? 'right' : 'left');
  }, [currentIdx, photos]);

  /* ── onTransitionEnd: advance to next card ── */
  const onCardExited = useCallback(() => {
    if (!exitDir) return;
    setExitDir(null);
    setDragX(0);
    setCurrentIdx(prev => prev + 1);
    requestAnimationFrame(() => {
      processingRef.current = false;
    });
  }, [exitDir]);

  // Safety fallback: if onTransitionEnd doesn't fire
  useEffect(() => {
    if (!exitDir) return;
    const timer = setTimeout(() => {
      if (processingRef.current) onCardExited();
    }, 400);
    return () => clearTimeout(timer);
  }, [exitDir, onCardExited]);

  /* ── Touch handlers ── */
  const onTouchStart = (e) => {
    if (processingRef.current) return;
    startXRef.current = e.touches[0].clientX;
  };

  const onTouchMove = (e) => {
    if (processingRef.current || startXRef.current === null) return;
    e.preventDefault();
    setDragX(e.touches[0].clientX - startXRef.current);
  };

  const onTouchEnd = () => {
    if (processingRef.current || startXRef.current === null) {
      startXRef.current = null;
      return;
    }
    if (dragX > 50) handleAction('coffre');
    else if (dragX < -50) handleAction('supprimer');
    else setDragX(0);
    startXRef.current = null;
  };

  /* ── Mouse handlers (desktop) ── */
  const onMouseDown = (e) => {
    if (processingRef.current) return;
    startXRef.current = e.clientX;
  };

  const onMouseMove = (e) => {
    if (processingRef.current || startXRef.current === null || e.buttons === 0) return;
    setDragX(e.clientX - startXRef.current);
  };

  const onMouseUp = () => {
    if (processingRef.current || startXRef.current === null) {
      startXRef.current = null;
      return;
    }
    if (dragX > 50) handleAction('coffre');
    else if (dragX < -50) handleAction('supprimer');
    else setDragX(0);
    startXRef.current = null;
  };

  /* ── Keyboard ── */
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'ArrowRight') handleAction('coffre');
      if (e.key === 'ArrowLeft') handleAction('supprimer');
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [handleAction]);

  /* ── Loading ── */
  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px' }}>
        <p style={{ fontFamily: serif, color: '#D4AF37', fontSize: 14 }}>Chargement des perles...</p>
      </div>
    );
  }

  /* ── Completed ── */
  if (currentIdx >= photos.length && photos.length > 0) {
    return (
      <div data-testid="tamis-complete" style={{ textAlign: 'center', padding: '48px 20px' }}>
        <div style={{
          marginBottom: 16,
          filter: 'drop-shadow(0 0 20px rgba(212,175,55,0.2))',
        }}>
          <TamisIcon size={120} />
        </div>
        <h3 style={{ fontFamily: serif, fontSize: 24, color: '#D4AF37', marginBottom: 6, fontWeight: 400, letterSpacing: 1 }}>
          Passage au Tamis termin&#233;
        </h3>
        <p style={{ fontFamily: serif, fontSize: 13, color: '#FAF3E0', marginBottom: 24, letterSpacing: 0.5 }}>
          Vos perles sont devenues des Tr&#233;sors
        </p>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 36, marginBottom: 28 }}>
          <div>
            <span style={{ fontFamily: serif, fontSize: 28, color: '#D4AF37', fontWeight: 500 }}>{stats.kept}</span>
            <p style={{ fontFamily: serif, fontSize: 12, color: '#FAF3E0', marginTop: 4 }}>Tr&#233;sors scell&#233;s</p>
          </div>
          <div>
            <span style={{ fontFamily: serif, fontSize: 28, color: '#FAF3E0', fontWeight: 500 }}>{stats.deleted}</span>
            <p style={{ fontFamily: serif, fontSize: 12, color: '#FAF3E0', marginTop: 4 }}>supprim&#233;s</p>
          </div>
        </div>
        <button onClick={onComplete} data-testid="tamis-done-btn" style={{
          fontFamily: serif, fontSize: 14, padding: '12px 32px', borderRadius: 8,
          background: 'linear-gradient(145deg, #D4AF37, #B8860B)', color: '#000', border: 'none', cursor: 'pointer', fontWeight: 600,
          boxShadow: '0 4px 20px rgba(212,175,55,0.3)', transition: 'all 0.3s',
        }}>
          Retour au Dashboard
        </button>
      </div>
    );
  }

  /* ── Empty sas ── */
  if (photos.length === 0) {
    return (
      <div data-testid="tamis-empty" style={{ textAlign: 'center', padding: '60px 20px' }}>
        <div style={{ opacity: 0.4, marginBottom: 16 }}>
          <TamisIcon size={70} />
        </div>
        <h3 style={{ fontFamily: serif, fontSize: 18, color: '#D4AF37', marginBottom: 8, fontWeight: 400 }}>Votre Sas de Tri est vide</h3>
        <p style={{ fontFamily: serif, fontSize: 13, color: '#FAF3E0' }}>Importez des perles pour commencer le tri.</p>
        <button onClick={onComplete} style={{
          fontFamily: serif, fontSize: 13, marginTop: 20, padding: '10px 24px', borderRadius: 8,
          color: '#D4AF37', backgroundColor: 'transparent', border: '1px solid rgba(212,175,55,0.3)', cursor: 'pointer',
        }}>Retour</button>
      </div>
    );
  }

  /* ── Card transforms ── */
  const exitDistance = (typeof window !== 'undefined' ? window.innerWidth : 800) + 200;
  const topX = isExiting ? (exitDir === 'right' ? exitDistance : -exitDistance) : dragX;
  const topRotate = isExiting ? (exitDir === 'right' ? 18 : -18) : dragX * 0.04;
  const topOpacity = isExiting ? 0 : 1;

  // Behind card subtle parallax: scales up as user drags
  const behindScale = 0.96 + Math.min(Math.abs(dragX) / 600, 0.04);
  const behindOpacity = 0.5 + Math.min(Math.abs(dragX) / 300, 0.5);

  return (
    <div data-testid="tamis-interface" style={{ padding: '0 16px', maxWidth: 500, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, padding: '0 4px' }}>
        <button onClick={onComplete} data-testid="tamis-back-btn" style={{
          fontFamily: montserrat, fontSize: 15, fontWeight: 700,
          color: '#D4AF37', background: 'none', border: 'none', cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 6, letterSpacing: 0.5,
          textShadow: '0 1px 8px rgba(212,175,55,0.3)',
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="3" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>
          Retour
        </button>
        <span data-testid="tamis-remaining" style={{
          fontFamily: montserrat, fontSize: 15, fontWeight: 700,
          color: '#D4AF37', letterSpacing: 0.5,
          textShadow: '0 1px 8px rgba(212,175,55,0.3)',
          backgroundColor: 'rgba(212,175,55,0.08)',
          border: '1px solid rgba(212,175,55,0.2)',
          padding: '4px 14px', borderRadius: 16,
        }}>
          {remaining} restant{remaining > 1 ? 's' : ''}
        </span>
      </div>

      {/* Card Stack */}
      <div
        style={{ position: 'relative', height: 420, marginBottom: 24, userSelect: 'none', touchAction: 'none' }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={() => {
          if (startXRef.current !== null && !processingRef.current) {
            setDragX(0);
            startXRef.current = null;
          }
        }}
      >
        {/* Behind card (next photo) — always visible underneath */}
        {nextPhoto && (
          <div
            key={`behind-${nextPhoto.id}`}
            style={{
              position: 'absolute', inset: 0, zIndex: 1,
              borderRadius: 16, overflow: 'hidden',
              border: '1px solid rgba(212,175,55,0.1)',
              backgroundColor: '#111',
              transform: `scale(${behindScale})`,
              opacity: behindOpacity,
              transition: isExiting ? 'transform 0.3s, opacity 0.3s' : 'none',
              pointerEvents: 'none',
            }}
          >
            <img
              src={`${(process.env.REACT_APP_BACKEND_URL || 'https://bobine.live')}${nextPhoto.image_url}?token=${token}`}
              alt="" draggable={false}
              style={{ width: '100%', height: '100%', objectFit: 'cover', pointerEvents: 'none' }}
            />
          </div>
        )}

        {/* Swipe indicators */}
        {dragX < -30 && !isExiting && (
          <div style={{
            position: 'absolute', left: 16, top: 16, zIndex: 10,
            backgroundColor: 'rgba(192,57,43,0.85)', color: '#fff',
            fontFamily: serif, fontSize: 14, padding: '6px 16px', borderRadius: 6, fontWeight: 600,
          }}>
            Supprimer
          </div>
        )}
        {dragX > 30 && !isExiting && (
          <div style={{
            position: 'absolute', right: 16, top: 16, zIndex: 10,
            backgroundColor: 'rgba(212,175,55,0.85)', color: '#000',
            fontFamily: serif, fontSize: 14, padding: '6px 16px', borderRadius: 6, fontWeight: 600,
          }}>
            Coffre-fort
          </div>
        )}

        {/* Current card (top, interactive) */}
        {currentPhoto && (
          <div
            key={currentPhoto.id}
            data-testid="tamis-photo-card"
            onTransitionEnd={(e) => {
              if (e.propertyName === 'transform' && isExiting) onCardExited();
            }}
            style={{
              position: 'absolute', inset: 0, zIndex: 2,
              borderRadius: 16, overflow: 'hidden',
              border: '1px solid rgba(212,175,55,0.2)',
              backgroundColor: '#111',
              transform: `translateX(${topX}px) rotate(${topRotate}deg)`,
              opacity: topOpacity,
              transition: isExiting ? 'transform 0.3s ease-out, opacity 0.25s ease-out' : 'none',
              cursor: isExiting ? 'default' : 'grab',
              touchAction: 'none',
              willChange: isExiting ? 'transform, opacity' : 'auto',
            }}
          >
            {/* Loading spinner while image downloads from kDrive */}
            {!imageLoaded && (
              <div data-testid="tamis-loading-spinner" style={{
                position: 'absolute', inset: 0, zIndex: 3,
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                backgroundColor: '#111', gap: 16,
              }}>
                <div style={{
                  width: 36, height: 36, borderRadius: '50%',
                  border: '2px solid rgba(212,175,55,0.15)',
                  borderTopColor: '#D4AF37',
                  animation: 'spin 0.8s linear infinite',
                }} />
                <p style={{ fontFamily: serif, fontSize: 12, color: '#D4AF37', margin: 0 }}>
                  Chargement du souvenir...
                </p>
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
              </div>
            )}
            <img
              src={`${(process.env.REACT_APP_BACKEND_URL || 'https://bobine.live')}${currentPhoto.image_url}?token=${token}`}
              alt="Souvenir" draggable={false}
              onLoad={() => setImageLoaded(true)}
              onError={() => setImageLoaded(true)}
              style={{ width: '100%', height: '100%', objectFit: 'cover', pointerEvents: 'none' }}
            />
            {/* Bottom overlay */}
            <div style={{
              position: 'absolute', bottom: 0, left: 0, right: 0, padding: '40px 16px 16px',
              background: 'linear-gradient(transparent, rgba(0,0,0,0.8))',
              pointerEvents: 'none',
            }}>
              {currentPhoto.days_remaining !== undefined && (
                <p style={{ fontFamily: serif, fontSize: 11, color: '#D4AF37', margin: 0 }}>
                  {currentPhoto.days_remaining} jour{currentPhoto.days_remaining !== 1 ? 's' : ''} avant disparition
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Action buttons */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: 40, marginBottom: 20 }}>
        <button onClick={() => handleAction('supprimer')} disabled={isExiting} data-testid="tamis-delete-btn" style={{
          width: 64, height: 64, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
          backgroundColor: 'rgba(192,57,43,0.1)', border: '2px solid rgba(192,57,43,0.5)', cursor: isExiting ? 'default' : 'pointer',
          transition: 'background-color 0.2s', opacity: isExiting ? 0.4 : 1,
        }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#c0392b" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>

        <button onClick={() => handleAction('coffre')} disabled={isExiting} data-testid="tamis-keep-btn" style={{
          width: 64, height: 64, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
          backgroundColor: 'rgba(212,175,55,0.1)', border: '2px solid rgba(212,175,55,0.5)', cursor: isExiting ? 'default' : 'pointer',
          transition: 'background-color 0.2s', opacity: isExiting ? 0.4 : 1,
        }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
        </button>
      </div>

      <p style={{ textAlign: 'center', fontFamily: serif, fontSize: 11, color: '#FAF3E0' }}>
        Glissez ou utilisez les boutons
      </p>
    </div>
  );
};

export default PassageAuTamis;
